import {
    Button,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
    withStyles,
} from '@material-ui/core';
import { useCallback, useEffect, useRef, useState } from 'react';
import { axiosInstance, cancelToken } from '../../utils/axiosInstance';
import { fwSignActivityCopy as copy, endpoint } from '../../utils/constants';
import { validActivityPayload } from '../../utils/fwEndpointBoard';
import { formatFwDate } from '../../utils/fwHelpers';
import { isCancelled } from '../../utils/fwWorkflow';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import TitleHeader from '../TitleHeader/TitleHeader';

const styles = {
    root: {
        height: '100%',
        boxSizing: 'border-box',
        padding: '1rem 2rem',
        display: 'flex',
        flexDirection: 'column',
    },
    note: { marginBottom: '0.5rem' },
    actions: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: '1rem',
        marginBottom: '1rem',
    },
    sign: { minWidth: '16rem' },
    section: { marginTop: '1.5rem' },
    table: {
        marginTop: '0.5rem',
        border: '1px solid #babfc7',
        borderRadius: 2,
        overflowX: 'auto',
    },
    headCell: {
        backgroundColor: '#f8f8f8',
        borderBottom: '1px solid #babfc7',
        fontWeight: 600,
        whiteSpace: 'nowrap',
    },
    cell: { borderBottom: '1px solid #dde2eb', whiteSpace: 'nowrap' },
    loadingWrapper: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        flex: 1,
    },
};

const recorded = value => (value === null || value === undefined || value === '' ? copy.notRecorded : value);

const columns = [
    { key: 'serial_number', header: copy.columns.serial },
    { key: 'task_id', header: copy.columns.task },
    { key: 'task_name', header: copy.columns.taskName },
    { key: 'attempt_id', header: copy.columns.attempt },
];

const ActivityTable = ({ classes, title, rows, truncated, empty, withCommand, keyPrefix, eventHeader }) => (
    <div className={classes.section}>
        <Typography variant="h6">{title}</Typography>
        {truncated && <Typography className={classes.note}>{copy.truncated}</Typography>}
        {rows.length === 0 && <Typography className={classes.note}>{empty}</Typography>}
        {rows.length > 0 && (
            <div className={classes.table}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            {columns.map(column => (
                                <TableCell key={column.key} className={classes.headCell}>
                                    {column.header}
                                </TableCell>
                            ))}
                            {withCommand && (
                                <TableCell className={classes.headCell}>{copy.columns.command}</TableCell>
                            )}
                            <TableCell className={classes.headCell}>{eventHeader}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map(row => (
                            <TableRow key={`${keyPrefix}-${row.endpoint_id}-${row.attempt_id}`}>
                                {columns.map(column => (
                                    <TableCell key={column.key} className={classes.cell}>
                                        {recorded(row[column.key])}
                                    </TableCell>
                                ))}
                                {withCommand && (
                                    <TableCell className={classes.cell}>{recorded(row.command_id)}</TableCell>
                                )}
                                <TableCell className={classes.cell}>{formatFwDate(row.event_at)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>
        )}
    </div>
);

const FwSignActivity = ({ classes }) => {
    const [sign, setSign] = useState('');
    const [loadedSign, setLoadedSign] = useState(null);
    const [active, setActive] = useState({ rows: [], truncated: false });
    const [pickup, setPickup] = useState({ rows: [], truncated: false });
    const [loading, setLoading] = useState(false);
    const [failed, setFailed] = useState(false);
    const [formError, setFormError] = useState('');
    const mounted = useRef(true);
    const source = useRef(null);

    const load = useCallback(async requested => {
        source.current?.cancel?.();
        const request = cancelToken.source();
        source.current = request;
        setLoading(true);
        setFailed(false);
        setFormError('');
        try {
            const response = await axiosInstance.get(
                `${endpoint.FW_READ_MODEL}/sign-activity?sign=${encodeURIComponent(requested)}&limit=200`,
                { cancelToken: request.token }
            );
            if (!mounted.current || request !== source.current) return;
            const data = response?.data?.data;
            if (!validActivityPayload(data, requested)) {
                setActive({ rows: [], truncated: false });
                setPickup({ rows: [], truncated: false });
                setFailed(true);
                return;
            }
            setActive(data.active);
            setPickup(data.pickup);
            setLoadedSign(requested);
        } catch (error) {
            if (!mounted.current || request !== source.current || isCancelled(error)) return;
            setActive({ rows: [], truncated: false });
            setPickup({ rows: [], truncated: false });
            setFailed(true);
        } finally {
            if (mounted.current && request === source.current) setLoading(false);
        }
    }, []);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
            source.current?.cancel?.();
        };
    }, []);

    const submit = event => {
        event.preventDefault();
        const requested = sign.trim();
        if (requested.length < 1 || requested.length > 64) {
            setFormError(copy.signRequired);
            return;
        }
        setLoadedSign(null);
        setActive({ rows: [], truncated: false });
        setPickup({ rows: [], truncated: false });
        load(requested);
    };

    const empty = active.rows.length === 0 && pickup.rows.length === 0;

    return (
        <div className={classes.root}>
            <TitleHeader title={`Moduł FW — ${copy.pageTitle}`} />
            <Typography className={classes.note}>{copy.explanation}</Typography>
            <Typography className={classes.note}>{copy.bounds}</Typography>
            <form className={classes.actions} onSubmit={submit}>
                <TextField
                    id="fw-sign-activity-sign"
                    className={classes.sign}
                    label={copy.signLabel}
                    value={sign}
                    onChange={event => setSign(event.target.value)}
                    error={Boolean(formError)}
                    helperText={formError}
                    inputProps={{ maxLength: 64 }}
                />
                <Button type="submit" variant="outlined" disabled={loading}>
                    {copy.show}
                </Button>
                {loadedSign && (
                    <Button variant="outlined" onClick={() => load(loadedSign)} disabled={loading}>
                        {copy.reload}
                    </Button>
                )}
            </form>
            {loading && (
                <div className={classes.loadingWrapper}>
                    <LoadingSpinner />
                </div>
            )}
            {!loading && failed && (
                <Typography className={classes.note} color="error" role="alert">
                    {copy.readError}
                </Typography>
            )}
            {!loading && !failed && loadedSign && empty && (
                <Typography className={classes.note}>{copy.empty}</Typography>
            )}
            {!loading && !failed && loadedSign && !empty && (
                <>
                    <ActivityTable
                        classes={classes}
                        title={copy.activeTitle}
                        rows={active.rows}
                        truncated={active.truncated}
                        empty={copy.activeEmpty}
                        withCommand={false}
                        keyPrefix="active"
                        eventHeader={copy.columns.activeEventAt}
                    />
                    <ActivityTable
                        classes={classes}
                        title={copy.pickupTitle}
                        rows={pickup.rows}
                        truncated={pickup.truncated}
                        empty={copy.pickupEmpty}
                        withCommand
                        keyPrefix="pickup"
                        eventHeader={copy.columns.pickupEventAt}
                    />
                </>
            )}
        </div>
    );
};

export default withStyles(styles)(FwSignActivity);
